import React, { useState, useEffect, useRef } from "react";
import { RiMenu5Line, RiCloseLine } from "react-icons/ri";
import { gsap } from "gsap";
import { Link } from "react-scroll";
import { useTranslation } from "react-i18next";
import LanguageToggle from "./LanguageToggle.jsx";

const NAV_ITEMS = [
  { key: "home", to: "home", offset: -80 },
  { key: "about", to: "about", offset: -60 },
  { key: "invite", to: "invite", offset: -60 },
  { key: "collaboration", to: "collaboration", offset: -60 },
  { key: "who", to: "chisono", offset: -60 }
];

const Navbar = () => {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const mobileMenuRef = useRef(null);
  const { t } = useTranslation();

  const handleNav = () => {
    setNavOpen((prev) => !prev);
    document.body.style.overflow = !navOpen ? "hidden" : "auto";
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuRef.current && navOpen) {
      gsap.fromTo(
        mobileMenuRef.current.children,
        { opacity: 0, y: -10 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.1,
          duration: 0.6,
          ease: "power2.out"
        }
      );
    }
  }, [navOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 font-Crimson transition-all duration-300 ${scrolled
        ? "bg-gradient-to-r from-[#D0ACA8]/50 via-[#B1C1B3]/40 to-[#537074]/50 shadow-lg border-b border-white/20"
        : "bg-transparent border-none"
        }`}
    >
      <div className="mx-auto px-4 sm:px-10 flex items-center justify-between h-12">
        <div
          className={`flex flex-1 items-center justify-between gap-8 transition-all duration-300 ${navOpen
            ? "opacity-0 -translate-y-full pointer-events-none"
            : "opacity-100 translate-y-0 pointer-events-auto"
            }`}
        >
          <h1 className="font-sans uppercase tracking-[0.2em] text-xs sm:text-sm text-white cursor-pointer select-none">
            {t("brand.name")}
          </h1>

          <div className="hidden min-[1080px]:flex items-center gap-8">
            <ul className="flex gap-8 text-lg text-white tracking-wide">
              {NAV_ITEMS.map((item) => (
                <li key={item.key}>
                  <Link
                    to={item.to}
                    smooth
                    duration={800}
                    offset={item.offset}
                    className="cursor-pointer hover:text-lime-300 transition-colors"
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
            <LanguageToggle />
          </div>
        </div>

        {!navOpen && (
          <div className="min-[1080px]:hidden relative z-50">
            <RiMenu5Line onClick={handleNav} className="text-white" size={25} />
          </div>
        )}
      </div>

      <div
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${navOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
        onClick={handleNav}
      ></div>

      <div
        className={`fixed top-0 right-0 w-[70%] max-w-[320px] h-screen bg-gradient-to-tr from-[#537074] to-[#537074]/70 shadow-2xl z-40 p-10 flex flex-col justify-center items-center transition-transform duration-500 ${navOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <button
          onClick={handleNav}
          className="absolute top-4 right-4 text-white/80 hover:text-white transition"
          aria-label="Chiudi menu"
        >
          <RiCloseLine size={24} />
        </button>

        <ul
          ref={mobileMenuRef}
          className="flex flex-col gap-8 text-white text-md text-center"
        >
          {NAV_ITEMS.map((item) => (
            <li key={item.key} onClick={handleNav}>
              <Link
                to={item.to}
                smooth
                duration={800}
                offset={item.offset}
                className="cursor-pointer hover:text-lime-300 transition-colors"
              >
                {t(`nav.${item.key}`)}
              </Link>
            </li>
          ))}
          <li className="pt-6">
            <LanguageToggle />
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
